import React, { useMemo } from 'react';
import './Measurements.css';

interface Measurement {
  id: string;
  timestamp: string;
  bodyFatPercentage?: number;
  muscleMass?: number;
  visceralFat?: number;
  bodyWater?: number;
  boneMass?: number;
  tricepsSkinfold?: number;
  bicepsSkinfold?: number;
  subscapularSkinfold?: number;
  suprailiacSkinfold?: number;
  abdominalSkinfold?: number;
  thighSkinfold?: number;
  calfSkinfold?: number;
  [key: string]: any;
}

interface AdvancedChartsProps {
  measurements: Measurement[];
}

export const AdvancedCharts: React.FC<AdvancedChartsProps> = ({ measurements }) => {
  const bioimpedanceData = useMemo(() => {
    return measurements
      .filter(m => m.bodyFatPercentage !== undefined || m.muscleMass !== undefined)
      .slice(0, 10)
      .reverse();
  }, [measurements]);

  const skinfoldData = useMemo(() => {
    return measurements
      .filter(m => 
        m.tricepsSkinfold !== undefined || 
        m.bicepsSkinfold !== undefined || 
        m.subscapularSkinfold !== undefined ||
        m.suprailiacSkinfold !== undefined ||
        m.abdominalSkinfold !== undefined ||
        m.thighSkinfold !== undefined ||
        m.calfSkinfold !== undefined
      )
      .slice(0, 10)
      .reverse();
  }, [measurements]);

  const renderBioimpedanceChart = () => {
    if (bioimpedanceData.length < 2) {
      return (
        <div className="ns-chart-empty">
          Dados insuficientes para exibir gráfico de bioimpedância
        </div>
      );
    }

    const maxValue = Math.max(
      ...bioimpedanceData.map(m => Math.max(
        m.bodyFatPercentage || 0,
        m.muscleMass || 0
      ))
    );
    const minValue = 0;
    const range = maxValue - minValue || 1;

    return (
      <div style={{ height: '250px', position: 'relative' }}>
        <svg width="100%" height="250" style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id="bodyFatGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="muscleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((val) => {
            const y = 20 + (220 - 20) - ((val - minValue) / range) * (220 - 20);
            return (
              <line
                key={val}
                x1="40"
                y1={y}
                x2="100"
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            );
          })}

          {/* Body Fat Line */}
          {bioimpedanceData.filter(m => m.bodyFatPercentage).length >= 2 && (() => {
            const bodyFatPoints = bioimpedanceData
              .filter(m => m.bodyFatPercentage)
              .map((m, i) => {
                const x = 40 + (i / (bioimpedanceData.length - 1)) * 60;
                const y = 20 + 200 - ((m.bodyFatPercentage! - minValue) / range) * 200;
                return { x, y, value: m.bodyFatPercentage! };
              });

            const bodyFatPath = bodyFatPoints.map((p, i) => 
              `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
            ).join(' ');

            const bodyFatArea = `${bodyFatPath} L ${bodyFatPoints[bodyFatPoints.length - 1].x},220 L ${bodyFatPoints[0].x},220 Z`;

            return (
              <>
                <path
                  d={bodyFatArea}
                  fill="url(#bodyFatGradient)"
                />
                <path
                  d={bodyFatPath}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {bodyFatPoints.map((p, i) => (
                  <circle
                    key={`bodyfat-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r="4"
                    fill="#ef4444"
                    stroke="#fff"
                    strokeWidth="2"
                  />
                ))}
              </>
            );
          })()}

          {/* Muscle Mass Line */}
          {bioimpedanceData.filter(m => m.muscleMass).length >= 2 && (() => {
            const musclePoints = bioimpedanceData
              .filter(m => m.muscleMass)
              .map((m, i) => {
                const x = 40 + (i / (bioimpedanceData.length - 1)) * 60;
                const y = 20 + 200 - ((m.muscleMass! - minValue) / range) * 200;
                return { x, y, value: m.muscleMass! };
              });

            const musclePath = musclePoints.map((p, i) => 
              `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
            ).join(' ');

            const muscleArea = `${musclePath} L ${musclePoints[musclePoints.length - 1].x},220 L ${musclePoints[0].x},220 Z`;

            return (
              <>
                <path
                  d={muscleArea}
                  fill="url(#muscleGradient)"
                />
                <path
                  d={musclePath}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {musclePoints.map((p, i) => (
                  <circle
                    key={`muscle-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r="4"
                    fill="#10b981"
                    stroke="#fff"
                    strokeWidth="2"
                  />
                ))}
              </>
            );
          })()}

          {/* Y-axis labels */}
          {[0, 25, 50, 75, 100].map((val) => {
            const y = 20 + 200 - ((val - minValue) / range) * 200;
            return (
              <text
                key={val}
                x="35"
                y={y + 4}
                fontSize="10"
                fill="#64748b"
                textAnchor="end"
              >
                {val}
              </text>
            );
          })}

          {/* X-axis labels */}
          {bioimpedanceData.map((m, i) => {
            if (i % Math.ceil(bioimpedanceData.length / 5) !== 0 && i !== bioimpedanceData.length - 1) return null;
            const x = 40 + (i / (bioimpedanceData.length - 1)) * 60;
            const date = new Date(m.timestamp);
            return (
              <text
                key={i}
                x={x}
                y="245"
                fontSize="9"
                fill="#64748b"
                textAnchor="middle"
              >
                {date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </text>
            );
          })}
        </svg>

        {/* Legend */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '12px', justifyContent: 'center' }}>
          {bioimpedanceData.some(m => m.bodyFatPercentage) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '3px', background: '#ef4444', borderRadius: '2px' }}></div>
              <span className="ns-chart-legend">% Gordura Corporal</span>
            </div>
          )}
          {bioimpedanceData.some(m => m.muscleMass) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '3px', background: '#10b981', borderRadius: '2px' }}></div>
              <span className="ns-chart-legend">Massa Muscular (kg)</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSkinfoldChart = () => {
    if (skinfoldData.length < 2) {
      return (
        <div className="ns-chart-empty">
          Dados insuficientes para exibir gráfico de dobras cutâneas
        </div>
      );
    }

    const skinfoldFields = [
      { key: 'tricepsSkinfold', label: 'Tríceps', color: '#0ea5e9' },
      { key: 'bicepsSkinfold', label: 'Bíceps', color: '#3b82f6' },
      { key: 'subscapularSkinfold', label: 'Subescapular', color: '#8b5cf6' },
      { key: 'suprailiacSkinfold', label: 'Suprailíaca', color: '#ec4899' },
      { key: 'abdominalSkinfold', label: 'Abdominal', color: '#f59e0b' },
      { key: 'thighSkinfold', label: 'Coxa', color: '#10b981' },
      { key: 'calfSkinfold', label: 'Panturrilha', color: '#14b8a6' },
    ];

    const allValues = skinfoldData.flatMap(m => 
      skinfoldFields.map(f => m[f.key] as number).filter(v => v !== undefined && v !== null)
    );
    const maxValue = Math.max(...allValues, 1);
    const minValue = 0;
    const range = maxValue - minValue || 1;

    return (
      <div style={{ height: '250px', position: 'relative' }}>
        <svg width="100%" height="250" style={{ overflow: 'visible' }}>
          {/* Grid lines */}
          {[0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue].map((val) => {
            const y = 20 + 200 - ((val - minValue) / range) * 200;
            return (
              <line
                key={val}
                x1="40"
                y1={y}
                x2="100"
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            );
          })}

          {/* Lines for each skinfold */}
          {skinfoldFields.map((field) => {
            const fieldData = skinfoldData
              .filter(m => m[field.key] !== undefined && m[field.key] !== null)
              .map((m, i) => {
                const x = 40 + (i / (skinfoldData.length - 1)) * 60;
                const y = 20 + 200 - ((m[field.key] as number - minValue) / range) * 200;
                return { x, y, value: m[field.key] as number };
              });

            if (fieldData.length < 2) return null;

            const path = fieldData.map((p, i) => 
              `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
            ).join(' ');

            return (
              <g key={field.key}>
                <path
                  d={path}
                  fill="none"
                  stroke={field.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.8"
                />
                {fieldData.map((p, i) => (
                  <circle
                    key={`${field.key}-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r="3"
                    fill={field.color}
                    stroke="#fff"
                    strokeWidth="1.5"
                  />
                ))}
              </g>
            );
          })}

          {/* Y-axis labels */}
          {[0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue].map((val) => {
            const y = 20 + 200 - ((val - minValue) / range) * 200;
            return (
              <text
                key={val}
                x="35"
                y={y + 4}
                fontSize="10"
                fill="#64748b"
                textAnchor="end"
              >
                {val.toFixed(0)}
              </text>
            );
          })}

          {/* X-axis labels */}
          {skinfoldData.map((m, i) => {
            if (i % Math.ceil(skinfoldData.length / 5) !== 0 && i !== skinfoldData.length - 1) return null;
            const x = 40 + (i / (skinfoldData.length - 1)) * 60;
            const date = new Date(m.timestamp);
            return (
              <text
                key={i}
                x={x}
                y="245"
                fontSize="9"
                fill="#64748b"
                textAnchor="middle"
              >
                {date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </text>
            );
          })}
        </svg>

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '12px', justifyContent: 'center' }}>
          {skinfoldFields.map(field => {
            const hasData = skinfoldData.some(m => m[field.key] !== undefined && m[field.key] !== null);
            if (!hasData) return null;
            return (
              <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '3px', background: field.color, borderRadius: '2px' }}></div>
                <span className="ns-chart-legend-sm">{field.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="ns-advanced-charts">
      {bioimpedanceData.length >= 2 && (
        <div className="ns-chart-container">
          <h3 className="ns-block-title">
            Evolução da Composição Corporal (Bioimpedância)
          </h3>
          {renderBioimpedanceChart()}
        </div>
      )}

      {skinfoldData.length >= 2 && (
        <div className="ns-chart-container" style={{ marginTop: '32px' }}>
          <h3 className="ns-block-title">
            Evolução das Dobras Cutâneas (mm)
          </h3>
          {renderSkinfoldChart()}
        </div>
      )}

      {bioimpedanceData.length < 2 && skinfoldData.length < 2 && (
        <div className="ns-chart-empty">
          <p>Dados insuficientes para exibir gráficos comparativos avançados.</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            Registre medições com dados de bioimpedância ou dobras cutâneas para visualizar os gráficos.
          </p>
        </div>
      )}
    </div>
  );
};

